/* eslint-disable react/jsx-key */
/* eslint-disable eqeqeq */
import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react";
import GoogleMapReact from "google-map-react";
import RoomIcon from "@mui/icons-material/Room";
import SchoolIcon from "@mui/icons-material/School";
import DirectionsBus from "@mui/icons-material/DirectionsBus";
import { useTranslation } from "react-i18next";

import { useForm } from "shared/hooks/form-hook";
import ErrorModal from "shared/components/UIElements/ErrorModal";
import Marker from "shared/components/UIElements/Marker";
import Player from "shared/components/UIElements/player";
import CustomSelect from "shared/components/FormElements/Select";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import { PlayerStates } from "shared/util/types";
import {
  TRAVEL_ACTION_TYPE,
  TRAVEL_ACTION_TYPE_EXPLANATIONS,
  APPROACH_TO_HOME_TYPE_EXPLANATIONS,
  ARRIVED_SCHOOL_TYPE_EXPLANATIONS,
} from "shared/util/types";

import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { useBundledRouteApi } from "features/bundled-routes/hooks/useBundledRouteApi";
import { formatIstanbulWallClock, getTodayInTimezone } from "shared/utility/date";
import RideSkipLivePanel from "features/ride-skip/RideSkipLivePanel";
import { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/constants";

import "./LiveItem.css";

// Track-Vehicle detail for the INDEPENDENT Bundled (multi-school) route. Isolated from the shared
// BaseLiveItem (which is coupled to VehicleRoute/PersonelService + the global store). Reuses the
// model-agnostic track-vehicle endpoints (travels by route, locations by travel) + dailyaction.
const isBundledPickUp = (route) => route?.routeType !== "DROP_OFF";

const validLoc = (loc) =>
  loc &&
  typeof loc.lat === "number" &&
  typeof loc.lng === "number" &&
  !(loc.lat === -1.1 && loc.lng === -1.1) &&
  !(loc.lat === 0 && loc.lng === 0);

const LiveBundledItem = ({ itemId }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { getBundledRoute } = useBundledRouteApi();
  const { isLoading, error, sendRequest, clearError } = useHttpClient();

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState(null);
  const [travels, setTravels] = useState([]);
  const [travelOptions, setTravelOptions] = useState([]);
  const [travelLocations, setTravelLocations] = useState([]);
  const [travelActions, setTravelActions] = useState([]);
  const [index, setIndex] = useState(0);

  const locationsREF = useRef([]);
  const mapREF = useRef(null);
  const mapsREF = useRef(null);
  const polylineREF = useRef(null);
  const indexREF = useRef(0);
  const playerStateREF = useRef(PlayerStates.NOTSTARTED);

  const [formState, inputHandler] = useForm({ travel: { value: "", isValid: false } }, true);

  const isPickUp = isBundledPickUp(loadedItem);

  // --- Students (home coords) and schools for map markers ---
  const students = useMemo(() => {
    const roster = Array.isArray(loadedItem?.student) ? loadedItem.student : [];
    return roster
      .map((s) => ({
        id: String(s.id || s._id),
        name: s.name,
        location: isPickUp ? s.locationgeton : s.locationgetoff,
      }))
      .filter((s) => validLoc(s.location));
  }, [loadedItem, isPickUp]);

  const schools = useMemo(() => {
    const list = Array.isArray(loadedItem?.schools) ? loadedItem.schools : [];
    return list.filter((sc) => validLoc(sc?.location));
  }, [loadedItem]);

  const clearPolylines = () => {
    if (polylineREF.current) {
      polylineREF.current.setMap(null);
      polylineREF.current = null;
    }
  };

  const drawPolylines = useCallback((currentlocations) => {
    clearPolylines();
    if (!mapREF.current || !mapsREF.current) return;
    if (!currentlocations || currentlocations.length === 0) return;
    const routePolyline = new mapsREF.current.Polyline({
      path: currentlocations,
      strokeColor: "#FF0000",
      strokeOpacity: 1,
      strokeWeight: 2,
    });
    routePolyline.setMap(mapREF.current);
    polylineREF.current = routePolyline;
    try {
      const bounds = new mapsREF.current.LatLngBounds();
      currentlocations.forEach((loc) => bounds.extend(new mapsREF.current.LatLng(loc.lat, loc.lng)));
      mapREF.current.fitBounds(bounds);
    } catch (e) {}
  }, []);

  const handleApiLoaded = (map, maps) => {
    mapREF.current = map;
    mapsREF.current = maps;
    drawPolylines(travelLocations?.map((item) => item?.location));
  };

  const fetchTravelLocations = useCallback(
    async (travelid) => {
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/track-vehicle/${travelid}`);
        setTravelLocations(responseData.travelLocations);
        locationsREF.current = responseData.travelLocations;
        drawPolylines(responseData.travelLocations.map((item) => item?.location));
      } catch (err) {}
    },
    [sendRequest, drawPolylines],
  );

  const fetchTravelActions = useCallback(
    async (travelid) => {
      if (!travelid) return;
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/dailyaction/${travelid}`);
        let currentActions = responseData.travelActions || [];
        currentActions = currentActions.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
        setTravelActions(currentActions);
        await fetchTravelLocations(travelid);
        setIndex(0);
      } catch (err) {}
    },
    [fetchTravelLocations, sendRequest],
  );

  const fetchTravels = useCallback(
    async (routeid) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/track-vehicle`,
          "POST",
          JSON.stringify({ routeid }),
          { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        );
        return responseData;
      } catch (err) {}
    },
    [sendRequest, auth.token],
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    const route = await getBundledRoute(itemId);
    setLoadedItem(route || null);
    if (!route) {
      setIsItemLoaded(true);
      return;
    }

    const fetched = await fetchTravels(itemId);
    const travelsList = fetched?.travels ?? [];
    setTravels(travelsList);

    // "Bugün" company timezone'una göre; Travel.day de aynı güne göre yazılıyor.
    const todayStr = getTodayInTimezone(auth.company?.timezone || auth.timezone);
    const reversed = travelsList.slice().reverse();
    const todayExists = reversed.some((tr) => tr.day === todayStr);
    setTravelOptions(
      todayExists
        ? reversed.map((tr) => ({ value: tr.id, label: tr.day }))
        : [{ value: "today", label: todayStr }, ...reversed.map((tr) => ({ value: tr.id, label: tr.day }))],
    );
    setIsItemLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const play = useCallback(() => {
    if (playerStateREF.current === PlayerStates.PLAYING) {
      if (indexREF.current === locationsREF.current.length - 1) {
        onIndexChangeHandler(0);
      } else {
        onIndexChangeHandler(indexREF.current + 1);
      }
    }
  }, []);

  useEffect(() => {
    const playerInterval = setInterval(() => play(), 500);
    return () => clearInterval(playerInterval);
  }, [play]);

  const handleTravelSelected = useCallback(
    async (travelid) => {
      if (travelid === "today") {
        setTravelActions([]);
        setTravelLocations([]);
        locationsREF.current = [];
        clearPolylines();
        return;
      }
      fetchTravelActions(travelid);
    },
    [fetchTravelActions],
  );

  const onIndexChangeHandler = (currentIndex) => {
    setIndex(() => currentIndex);
    indexREF.current = currentIndex;
  };
  const onPlayerStateChangeHandler = (currentState) => {
    playerStateREF.current = currentState;
  };
  const onFullScreenChangeHandler = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const getMapOptions = (maps) => ({
    streetViewControl: false,
    scaleControl: true,
    fullscreenControl: true,
    styles: [
      { stylers: [{ visibility: "on" }] },
      { featureType: "road", elementType: "labels", stylers: [{ visibility: "on" }] },
      { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    ],
    gestureHandling: "greedy",
    minZoom: 11,
    maxZoom: 25,
    mapTypeControl: false,
    mapTypeId: maps.MapTypeId.ROADMAP,
    zoomControl: false,
    clickableIcons: false,
  });

  const mapPoints = useMemo(
    () => (Array.isArray(travelLocations) ? travelLocations.map((i) => i?.location).filter(Boolean) : []),
    [travelLocations],
  );

  const defaultCenter = useMemo(() => {
    if (mapPoints.length === 0) return { lat: 39, lng: 35 };
    let minLat = mapPoints[0].lat,
      maxLat = mapPoints[0].lat,
      minLng = mapPoints[0].lng,
      maxLng = mapPoints[0].lng;
    for (let i = 1; i < mapPoints.length; i++) {
      const p = mapPoints[i];
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
    return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  }, [mapPoints]);

  useEffect(() => {
    drawPolylines(travelLocations?.map((item) => item?.location));
  }, [travelLocations, drawPolylines]);

  const isTodayWithoutTravel = formState.inputs.travel.value === "today";

  const studentName = (studentId) => (loadedItem?.student || []).find((s) => (s.id || s._id) == studentId)?.name || "";

  if (!isItemLoaded) {
    return <div className="center">{<LoadingSpinner />}</div>;
  }

  if (!loadedItem) {
    return <div className="center width-full">{t("noHistoryForRoute")}</div>;
  }

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      {travels.length != 0 ? (
        <div className="vertical-flex" style={{ justifyContent: "flex-start" }}>
          <div
            className="card"
            style={{
              display: "flex",
              width: "100%",
              margin: "0 auto",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              columnGap: "20px",
              flexWrap: "wrap",
              marginTop: "0px",
              paddingTop: "0px",
              paddingBottom: "0px",
              marginBottom: "0px",
            }}
          >
            <div
              style={{
                display: "flex",
                marginTop: "10px",
                alignItems: "baseline",
                marginBottom: "0px",
                flexDirection: "row",
                columnGap: "10px",
                minWidth: "400px",
              }}
            >
              <h4 style={{ marginBottom: "0", marginTop: "0" }}>{t("selectRouteDate")}</h4>
              <CustomSelect
                options={travelOptions}
                id="travel"
                initialValue={travelOptions.length > 0 ? travelOptions[0] : undefined}
                onInput={inputHandler}
                initialValid={true}
                fireChange={handleTravelSelected}
              ></CustomSelect>
            </div>
            <span>
              <strong>{t("bundledRoutes.tabTitle")}:</strong> {loadedItem.name} (
              {isPickUp ? t("pickUp") : t("dropOff")})
            </span>
            <span>
              <strong>{t("bundledRoutes.schoolCount")}: </strong>
              {schools.length}
            </span>
            <span>
              <strong>{t("driver")}: </strong>
              {loadedItem?.driver?.[0]?.name || "-"}
            </span>
            {loadedItem?.plate ? (
              <span>
                <strong>{t("plate")}: </strong>
                {loadedItem.plate}
              </span>
            ) : null}
          </div>

          {isTodayWithoutTravel ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                color: "#666",
                width: "100%",
              }}
            >
              <p style={{ fontSize: "16px", fontWeight: "bold", margin: "0 0 8px 0" }}>{t("noTravelToday")}</p>
              <p style={{ fontSize: "14px", margin: 0 }}>{t("selectPreviousDay")}</p>
            </div>
          ) : (
            <div className="live-item-right-side">
              <div className="middle-container">
                <div className="player-container">
                  <div className="live-item-map-body">
                    {travelLocations.length > 0 && !isLoading && (
                      <div className="live-item-map-player">
                        <Player
                          onPlayerStateChange={onPlayerStateChangeHandler}
                          onPlayerIndexChange={onIndexChangeHandler}
                          onFullScreenChange={onFullScreenChangeHandler}
                          min={0}
                          max={travelLocations.length - 1}
                          value={index}
                          text={formatIstanbulWallClock(locationsREF.current[index]?.date, auth.timezone)}
                        />
                      </div>
                    )}

                    {!isLoading && travelLocations.length > 0 ? (
                      <div className="live-item-map-canvas">
                        <GoogleMapReact
                          options={getMapOptions}
                          key={itemId}
                          yesIWantToUseGoogleMapApiInternals
                          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                          defaultCenter={defaultCenter}
                          defaultZoom={13}
                        >
                          {locationsREF.current?.length && (
                            <Marker
                              Icon={DirectionsBus}
                              lat={locationsREF.current[index]?.location.lat}
                              lng={locationsREF.current[index]?.location.lng}
                              key={"bundled-bus"}
                              info={
                                formatIstanbulWallClock(locationsREF.current[index]?.date, auth.timezone) +
                                " - " +
                                Math.floor((locationsREF.current[index]?.speed || 0) * 3.6) +
                                " " +
                                t("kmhour")
                              }
                              alwaysShowMarker={true}
                              initialShow={true}
                            />
                          )}
                          {schools.map((sc) => (
                            <Marker
                              Icon={SchoolIcon}
                              lat={sc.location.lat}
                              lng={sc.location.lng}
                              key={`school-${sc.id || sc._id}`}
                              info={sc.name}
                              alwaysShowMarker={true}
                            />
                          ))}
                          {students.map((student) => (
                            <Marker
                              Icon={RoomIcon}
                              lat={student.location.lat}
                              lng={student.location.lng}
                              key={student.id}
                              info={student.name}
                              alwaysShowMarker={false}
                              initialShow={false}
                            />
                          ))}
                        </GoogleMapReact>
                      </div>
                    ) : (
                      !isLoading && <div className="center width-full">{t("noHistoryForRoute")}</div>
                    )}
                  </div>
                </div>

                <div className="actions-container">
                  <RideSkipLivePanel
                    serviceKind={RIDE_SKIP_SERVICE_KINDS.BUNDLED_ROUTE}
                    serviceId={itemId}
                    students={loadedItem?.student || []}
                  />
                  {travelActions.length != 0 && (
                    <div>
                      <div
                        style={{
                          paddingBottom: "10px",
                          backgroundColor: "white",
                          position: "sticky",
                          zIndex: 1,
                          top: 0,
                        }}
                      >
                        <h4 style={{ margin: 0 }}>{t("routeMovements")}</h4>
                      </div>
                      <div>
                        {travelActions.map((travelaction) => (
                          <div>
                            <div
                              style={{
                                background:
                                  locationsREF.current[index]?.date?.split(" ")[1] >= travelaction?.date?.split(" ")[1]
                                    ? "ForestGreen"
                                    : "Gray",
                                marginBottom: "5px",
                                color: "white",
                                padding: "10px",
                                borderRadius: "5px",
                                fontSize: "14px",
                              }}
                            >
                              {formatIstanbulWallClock(travelaction?.date, auth.timezone)} {" - "}
                              {(travelaction.actiontype === TRAVEL_ACTION_TYPE.STARTED ||
                                travelaction.actiontype === TRAVEL_ACTION_TYPE.FINISHED) &&
                                t(
                                  TRAVEL_ACTION_TYPE_EXPLANATIONS.find((exp) => exp.value === travelaction.actiontype)
                                    ?.label ?? "-",
                                )}
                              {travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME && (
                                <>
                                  {studentName(travelaction.student)}
                                  <br />
                                  {t(
                                    APPROACH_TO_HOME_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                      ?.label ?? "-",
                                  )}
                                </>
                              )}
                              {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL && (
                                <>
                                  {studentName(travelaction.student)}
                                  <br />
                                  {t(
                                    ARRIVED_SCHOOL_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                      ?.label ?? "-",
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="center width-full">{t("noHistoryForRoute")}</div>
      )}
    </React.Fragment>
  );
};

export default LiveBundledItem;
